import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';

import InputPasswordToggle from '@components/input-password-toggle';
import { useBrand } from '@hooks/useBrand';
import { Loading } from '@src/@core/components/loading';

import logo from '@src/assets/images/logo/logo.png';

import useLoginForm from './useLoginForm';

import '@styles/base/pages/page-auth.scss';

const LoginForm: React.FC = () => {
  const { brand, nameTab } = useBrand();
  const [blocking, setBlocking] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  function handleChangeBlocking(isBlocking: boolean): void {
    setBlocking(isBlocking);
  }

  const { register, errors, onSubmit } = useLoginForm({
    onChangeBlocking: handleChangeBlocking,
    setRefreshReCaptcha,
    refreshReCaptcha,
  });

  useEffect(() => {
    document.title = `Login | ${nameTab}`;
  }, [nameTab]);

  return (
    <>
      <div className="auth-wrapper auth-v1 px-2">
        <div className="auth-inner py-2">
          <Card className="mb-0">
            <CardBody>
              <Link
                className="brand-logo"
                to="/"
                onClick={(e) => e.preventDefault()}
              >
                {nameTab.toLowerCase() !== 'ultrapayments' ? (
                  <img
                    src={brand}
                    alt={`Logo ${nameTab}`}
                    className="img-fluid"
                    style={{ width: '189px' }}
                  />
                ) : (
                  <img
                    src={logo}
                    alt="Logo Ultrapayments"
                    className="img-fluid"
                  />
                )}
              </Link>
              <CardTitle tag="h4" className="mb-1">
                {nameTab.toLowerCase() === 'g8pay' ? (
                  <> Bem vindo a G8 PAY 👋</>
                ) : (
                  <> Bem vindo a {nameTab} 👋</>
                )}
              </CardTitle>
              <CardText className="mb-2">
                Faça login em sua conta e comece a aventura.
              </CardText>
              <Form className="auth-login-form mt-2" onSubmit={onSubmit}>
                <FormGroup>
                  <Label className="form-label" for="email">
                    Email
                  </Label>
                  <Input
                    autoFocus
                    type="email"
                    id="email"
                    name="email"
                    placeholder="example@example.com"
                    className={classnames({
                      'is-invalid': errors.email,
                    })}
                    innerRef={register({ required: true })}
                  />
                  {errors && errors.email && (
                    <FormFeedback>{errors.email.message}</FormFeedback>
                  )}
                </FormGroup>
                <FormGroup>
                  <div className="d-flex justify-content-between">
                    <Label className="form-label" for="password">
                      Senha
                    </Label>
                    <Link to="/forgot-password">
                      <small>Esqueceu a senha?</small>
                    </Link>
                  </div>
                  <InputPasswordToggle
                    id="password"
                    name="password"
                    className={`input-group-merge ${classnames({
                      'is-invalid': errors.password,
                    })}`}
                    innerRef={register({ required: true })}
                  />
                  {errors && errors.password && (
                    <FormFeedback>{errors.password.message}</FormFeedback>
                  )}
                </FormGroup>
                <Button type="submit" color="primary" block>
                  Entrar
                </Button>
              </Form>
              {process.env.REACT_APP_DEVELOPMENT &&
                process.env.REACT_APP_DEVELOPMENT === 'true' && (
                  <p className="text-center mt-2">
                    <span className="mr-25">Não tem cadastro?</span>
                    <Link to="/register">
                      <span>Cadastre-se</span>
                    </Link>
                  </p>
                )}
            </CardBody>
          </Card>
        </div>
      </div>
      <Loading blocking={blocking} />
    </>
  );
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_API_RECAPTCHA_SITE_KEY!;

export const Login: React.FC = () => (
  <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
    <LoginForm />
  </GoogleReCaptchaProvider>
);
